
import { computed, defineComponent, ref } from "vue";
import { hideModal } from "@/core/helpers/dom";
import { useI18n } from "vue-i18n/index";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import moment from "moment";

interface Step1 {
  type: string;
}

interface Step2 {
  rand_number: number;
  release_date: string;
  due_date: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  products: any[];
  public_note: string;
  private_note: string;
  has_discount: number;
  discount_amount: string;
  discount_type: string;
  invoice_tax: number;
  customer_id: string;
  payment_id: string;
  shipping_company_id: string;
  account_id: string;
  invoice_status_id: number;
}

interface KTCreateApp extends Step1, Step2 {}

interface Customer {
  id: number;
  name: string;
}

export default defineComponent({
  name: "create-invoice-modal",
  setup() {
    const createInvoiceRef = ref<HTMLFormElement>();
    const createInvoiceModalRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(1);
    const paymentMethod = ref(false);
    const toggleNotes = ref(false);
    const paymentUrl = ref("");
    const paidInvoice = ref(false);
    const customers = ref([] as Customer[]);
    const il8n = useI18n();
    const store = useStore();

    const formData = ref<KTCreateApp>({
      type: "simple",
      rand_number: Math.floor(Math.random() * 1000000),
      release_date: "",
      due_date: "",
      products: [
        {
          product_name: "",
          product_desc: "",
          product_quantity: "1",
          product_price: "",
        },
      ],
      public_note: "",
      private_note: "",
      has_discount: 0,
      discount_amount: "",
      discount_type: "fixed",
      invoice_tax: 14,
      customer_id: "",
      payment_id: "",
      shipping_company_id: "",
      account_id: "",
      invoice_status_id: 1,
    });

    const addItem = () => {
      formData.value.products.push({
        product_name: "",
        product_desc: "",
        product_quantity: "1",
        product_price: "",
      });
    };

    const removeItem = (index: number) => {
      formData.value.products.splice(index, 1);
    };

    const totalInvoice = computed(() => {
      let total = 0;
      formData.value.products.forEach((item) => {
        total +=
          item.product_price && item.product_quantity
            ? parseFloat(item.product_quantity) * parseFloat(item.product_price)
            : 0;
      });

      return total;
    });

    const user = computed(() => {
      return store.getters.currentUser;
    });

    const createInvoice = () => {
      if (!createInvoiceRef.value) return;

      createInvoiceRef.value.validate((valid) => {
        if (valid) {
          if (formData.value.discount_amount) {
            formData.value.has_discount = 1;
          } else {
            formData.value.has_discount = 0;
          }

          if (paidInvoice.value) {
            formData.value.invoice_status_id = 2;
          } else {
            formData.value.invoice_status_id = 1;
          }

          Swal.fire({
            title: il8n.t("pleaseWait"),
            text: il8n.t("creatingAccount"),
            showConfirmButton: false,
            allowOutsideClick: false,
            onBeforeOpen: () => {
              Swal.showLoading();
            },
          });

          store
            .dispatch(Actions.CREATE_INVOICE, formData.value)
            .then((response) => {
              Swal.close();
              Swal.fire({
                title: il8n.t("success"),
                text: response.message,
                icon: "success",
                showConfirmButton: false,
                timer: 1500,
              }).then(() => {
                hideModal(createInvoiceModalRef.value);

                currentStepIndex.value = 1;
                createInvoiceRef.value?.resetFields();
                formData.value.rand_number = Math.floor(
                  Math.random() * 1000000
                );
              });
            })
            .catch(() => {
              const message = store.getters.getInvoiceErrors;
              Swal.close();
              Swal.fire({
                title: il8n.t("error"),
                text: message.data.message,
                icon: "error",
                showConfirmButton: false,
                timer: 1500,
              });
            });
        }
      });
    };

    return {
      createInvoiceRef,
      createInvoice,
      paymentMethod,
      paymentUrl,
      toggleNotes,
      paidInvoice,
      addItem,
      removeItem,
      currentStepIndex,
      totalInvoice,
      il8n,
      formData,
      user,
      customers,
      createInvoiceModalRef,
      moment,
    };
  },
});
